import React from "react";

import Badge from "../Badge/Badge";
import Hyperlink from "../Hyperlink/Hyperlink";
import ResourceStatus, { StatusValues } from "../ResourceStatus/ResourceStatus";

import styles from "./GlobalCard.module.css";
import { CellContentProps } from "../SearchResultsTable/BodyCell";
import { componentNanoid } from "../../utils/componentNanoid";
import { RowData } from "../SearchResultsTable/BodyRow";
import convertCharRefs from "../../utils/convertCharReferences";
import { useSearchQuestion } from "../../context/DataContext";
import filterQueryWord from "../../utils/filterQueryWords";

export type CardLinkProps = {
    children: React.ReactNode;
    className: string;
    href?: string;
};

export type CardProps = {
    parts: RowData;
};

type CardTextProps = {
    cellData: string;
    query: string | null;
};

/**
 * Renders a card text component with optional highlighting based on a query.
 *
 * @param props - The component props.
 * @param props.cellData - The text content to be rendered.
 * @param props.query - The string to be highlighted (optional).
 * @returns The rendered card text component.
 */
export const CardText = ({ cellData, query }: CardTextProps) => {
    if (!cellData) return null;

    const text = convertCharRefs(cellData.trim().replace("\\n", "\n"));

    if (query) {
        const words = query.split(" ").filter(Boolean).filter(filterQueryWord);
        const regex = new RegExp(`(${words.join("|")})`, "gi");
        const parts = text.split(regex);
        return (
            <div className={styles.description}>
                {parts.map((part, index) =>
                    regex.test(part) ? (
                        // Wrap matching parts in a <strong> element for highlighting
                        <strong key={index}>{part}</strong>
                    ) : (
                        part
                    )
                )}
            </div>
        );
    }

    return <div className={styles.description}>{text}</div>;
};

const CardContent = ({ cellDisplay = "text", data, url }: CellContentProps) => {
    const { query } = useSearchQuestion();
    const cellData = data?.trim() ?? "";
    switch (cellDisplay) {
        case "badge":
            return <>{cellData ? <Badge label={cellData} /> : null}</>;
        case "status":
            return (
                <>
                    {cellData ? (
                        <div className={styles.status}>
                            <ResourceStatus status={cellData as StatusValues} />
                        </div>
                    ) : null}
                </>
            );
        case "textLink":
            return (
                <Hyperlink href={url} className={styles.headingLink}>
                    {cellData}
                </Hyperlink>
            );
        case "title":
            return <h2 className={styles.heading}>{cellData}</h2>;
        default:
            return <CardText cellData={cellData} query={query} />;
    }
};

const GlobalCard = ({ parts }: CardProps) => {
    return (
        <li className={styles.card}>
            {parts?.length &&
                parts.map((part) => (
                    <CardContent
                        {...(part as CellContentProps)}
                        key={`card-${componentNanoid()}`}
                    />
                ))}
        </li>
    );
};

export default GlobalCard;
